import tinycolor from 'tinycolor2';

// Style propre au numéro d'un niveau (configuration.titleN.indexStyle). Ces
// valeurs sont neutres : le numéro reprend la police, la taille et la couleur
// du titre, comme avant l'option.
export const DEFAULT_TITLE_INDEX_STYLE = {
  fontFamily: '', // '' : police du titre
  fontSize: null, // null : taille du titre
  fontWeight: '', // '' : graisse du titre
  color: null, // null : couleur du titre, sinon { label, style } de la palette
  opacity: 100, // %, appliquée à la couleur tracée (remplissage ou contour)
  outline: false, // contour seul, intérieur transparent
  outlineWidth: 2, // px
};

// Rendu du texte d'un niveau (configuration.titleN.textEffect), mêmes réglages
// que ceux du numéro. Neutres : le titre garde son rendu d'avant l'option.
export const DEFAULT_TITLE_TEXT_EFFECT = {
  opacity: 100, // %, appliquée à la couleur tracée (remplissage ou contour)
  outline: false, // contour seul, intérieur transparent
  outlineWidth: 2, // px
};

const withOpacity = (color, opacityPercent) => {
  const parsed = tinycolor(color);
  return parsed.setAlpha((parsed.getAlpha() * opacityPercent) / 100).toRgbString();
};

/**
 * CSS du rendu du texte d'un titre (contour, opacité), posé sur la ligne : le
 * numéro en hérite tant qu'il n'a pas de couleur à lui. Vide sans réglage.
 */
export const getTitleTextStyle = (titleConfig) => {
  const effect = { ...DEFAULT_TITLE_TEXT_EFFECT, ...titleConfig?.textEffect };
  const opacity = Math.min(100, Math.max(0, Number(effect.opacity ?? 100)));
  const baseColor = titleConfig?.color?.style;
  if (!baseColor || (!effect.outline && opacity === 100)) return {};
  const drawnColor = withOpacity(baseColor, opacity);
  if (effect.outline) {
    return {
      color: 'transparent',
      WebkitTextStroke: `${Number(effect.outlineWidth) || DEFAULT_TITLE_TEXT_EFFECT.outlineWidth}px ${drawnColor}`,
    };
  }
  return { color: drawnColor };
};

/**
 * CSS du bloc numéro. Vide tant qu'aucun réglage n'est posé : le numéro hérite
 * alors de la ligne du titre.
 */
export const getTitleIndexStyle = (titleConfig) => {
  const indexStyle = { ...DEFAULT_TITLE_INDEX_STYLE, ...titleConfig?.indexStyle };
  const style = {};
  if (indexStyle.fontFamily) style.fontFamily = indexStyle.fontFamily;
  if (indexStyle.fontSize) {
    style.fontSize = indexStyle.fontSize;
    // La ligne du titre a un interligne de 1.4 × la taille du texte ; hérité,
    // il rendrait un grand numéro bien plus haut que ses glyphes.
    style.lineHeight = 1;
  }
  if (indexStyle.fontWeight) style.fontWeight = indexStyle.fontWeight;

  const opacity = Math.min(100, Math.max(0, Number(indexStyle.opacity ?? 100)));
  const baseColor = indexStyle.color?.style || titleConfig?.color?.style;
  if (!baseColor || (!indexStyle.outline && !indexStyle.color && opacity === 100)) {
    return style;
  }
  const drawnColor = withOpacity(baseColor, opacity);
  if (indexStyle.outline) {
    style.color = 'transparent';
    style.WebkitTextStroke = `${Number(indexStyle.outlineWidth) || DEFAULT_TITLE_INDEX_STYLE.outlineWidth}px ${drawnColor}`;
  } else {
    style.color = drawnColor;
    // Numéro plein sous un titre en contour : il hériterait sinon du trait du
    // titre par-dessus sa propre couleur.
    if (titleConfig?.textEffect?.outline) style.WebkitTextStroke = 'initial';
  }
  return style;
};

// titleN.textAlign → position du bloc numéro + texte dans la ligne.
const HORIZONTAL_ALIGNMENTS = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

// titleN.verticalAlignment → position du numéro face au texte.
const VERTICAL_ALIGNMENTS = {
  baseline: 'baseline',
  top: 'flex-start',
  center: 'center',
  bottom: 'flex-end',
};

/**
 * Disposition d'une ligne de titre : le numéro (index) puis le texte.
 *
 * - textAlign : avec un numéro, c'est le bloc numéro + texte qui se déplace. Le
 *   texte cesse alors de remplir la largeur restante, sinon le numéro resterait
 *   collé à gauche.
 * - verticalAlignment : position du numéro face à un titre sur plusieurs lignes.
 * - textOffset (px) : rapproche le texte du numéro ; négatif, il le chevauche.
 *   Ignoré sans numéro : le texte sortirait de la box.
 *
 * Les documents antérieurs n'ont pas ces clés : on retombe sur l'ancien rendu
 * (gauche, ligne de base, aucun décalage).
 */
export const getTitleLayout = (titleConfig, { hasIndex, indexGap = 0 } = {}) => {
  const textAlign = HORIZONTAL_ALIGNMENTS[titleConfig?.textAlign]
    ? titleConfig.textAlign
    : 'left';
  const shrinkText = hasIndex && textAlign !== 'left';
  return {
    textAlign,
    row: {
      justifyContent: HORIZONTAL_ALIGNMENTS[textAlign],
      alignItems: VERTICAL_ALIGNMENTS[titleConfig?.verticalAlignment] || 'baseline',
    },
    index: { paddingRight: indexGap, ...getTitleIndexStyle(titleConfig) },
    text: {
      flex: shrinkText ? '0 1 auto' : 1,
      // Largeur minimale : un titre vide réduit à 0px n'offrirait plus de zone
      // où cliquer.
      minWidth: shrinkText ? '1ch' : 0,
      marginLeft: hasIndex ? Number(titleConfig?.textOffset) || 0 : 0,
    },
  };
};

// Le libellé « transparent » de la palette ne pose aucun fond, quelle que soit
// sa valeur CSS.
export const getTitleBackgroundColor = (backgroundColor) =>
  backgroundColor?.label !== 'transparent' ? backgroundColor?.style : null;

export const getTitleStyle = (backgroundColor, border, style, color, rest) => {
  return {
    ...style,
    ...rest,
    color,
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    backgroundClip: 'content-box',
    backgroundColor: getTitleBackgroundColor(backgroundColor),
    borderTop:
      border?.top === true
        ? border?.size + 'px solid ' + border?.color?.style
        : null,
    borderBottom:
      border?.bottom === true
        ? border?.size + 'px solid ' + border?.color.style
        : null,
    borderLeft:
      border?.left === true
        ? border?.size + 'px solid ' + border?.color.style
        : null,
    borderRight:
      border?.right === true
        ? border?.size + 'px solid ' + border?.color.style
        : null,
  };
};
